import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../lib/store';
import type { AvatarOption, AvatarPickerPayload } from '../../lib/types';
import { Button, ErrorState, LoadingGrid } from '../ui';

/**
 * "Choose your Trainer."
 *
 * A character-select screen, not a settings form. That framing decides most of
 * what follows: a grid of actual sprites at a size you can see rather than a
 * dropdown of filenames, a large preview before committing, and a Select button
 * that is a deliberate act. The brief's list of things to avoid — generic SaaS
 * settings UI, circular social-media avatars, huge modern photo avatars — is
 * really one instruction: these are game characters, so present them as game
 * characters.
 *
 * ## What the server decides and what this decides
 *
 * The server decides everything that matters. Which avatars exist, which are
 * unlocked, which are reserved for Living Trainers, and whether a selection is
 * allowed — all of it comes back on the payload or comes back as a refusal.
 * This component decides only what to draw and in what order. A grid built from
 * a client-side list of filenames is a grid that can offer something the save
 * endpoint refuses, which is the failure `game/cosmetics.ts` exists to prevent.
 *
 * ## Performance
 *
 * Paged at the API and lazily loaded in the browser, because the catalog is
 * meant to reach hundreds. `loading="lazy"` on every tile means a first paint
 * costs the visible rows only; "Show more" appends rather than replacing, so
 * scrolling back never re-fetches what you have already seen.
 */

const PAGE = 48;

/** The filter chips, in the order the brief asks for them. */
const CATEGORY_FILTERS: Array<{ label: string; category?: string; generation?: number }> = [
  { label: 'All' },
  { label: 'Gen 1', generation: 1 },
  { label: 'Gen 2', generation: 2 },
  { label: 'Gym Leaders', category: 'GYM_LEADER' },
  { label: 'Trainer Classes', category: 'TRAINER_CLASS' },
  { label: 'Elite Four', category: 'ELITE_FOUR' },
  { label: 'Special', category: 'SPECIAL' },
];

const PRESENTATIONS = [
  { value: '', label: 'Anyone' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];

export interface AvatarPickerProps {
  /** Called after a successful save, so the shell can refresh the session. */
  onSelected?: (avatar: AvatarOption | null) => void;
}

export function AvatarPicker({ onSelected }: AvatarPickerProps) {
  const { push } = useToast();

  const [filter, setFilter] = useState(0);
  const [search, setSearch] = useState('');
  const [presentation, setPresentation] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [trainerClass, setTrainerClass] = useState('');

  const [page, setPage] = useState<AvatarPickerPayload | null>(null);
  const [rows, setRows] = useState<AvatarOption[]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** The tile being *considered*. Selecting is a second, deliberate click. */
  const [preview, setPreview] = useState<AvatarOption | null>(null);

  // Debounced, because this is a search box over a few hundred rows and a
  // request per keystroke is a request per keystroke.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const active = CATEGORY_FILTERS[filter]!;

  // A ref rather than state: it exists only to discard a response that has been
  // overtaken by a newer one, and storing it in state would re-render for a
  // value nothing draws.
  const requestId = useRef(0);

  useEffect(() => {
    setOffset(0);
  }, [debounced, filter, presentation, favoritesOnly, trainerClass]);

  useEffect(() => {
    const id = (requestId.current += 1);
    setLoading(true);
    const params = new URLSearchParams();
    if (debounced) params.set('search', debounced);
    if (active.generation) params.set('generation', String(active.generation));
    if (active.category) params.set('category', active.category);
    if (presentation) params.set('presentation', presentation);
    if (trainerClass) params.set('trainerClass', trainerClass);
    if (favoritesOnly) params.set('favorites', 'true');
    params.set('limit', String(PAGE));
    params.set('offset', String(offset));

    api
      .get<AvatarPickerPayload>(`/avatars?${params.toString()}`)
      .then((data) => {
        if (requestId.current !== id) return;
        setPage(data);
        // Appended on "Show more", replaced on any filter change — which is
        // exactly what `offset === 0` means here.
        setRows((current) => (offset === 0 ? data.avatars : [...current, ...data.avatars]));
        setError(null);
      })
      .catch((caught) => {
        if (requestId.current !== id) return;
        setError(caught instanceof ApiError ? caught.message : 'Could not load avatars.');
      })
      .finally(() => {
        if (requestId.current === id) setLoading(false);
      });
  }, [debounced, active.generation, active.category, presentation, trainerClass, favoritesOnly, offset]);

  const selectedId = page?.selectedId ?? null;

  const current = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? null,
    [rows, selectedId],
  );

  // What the big panel shows: whatever is being considered, otherwise whatever
  // is already worn. A preview pane that empties when you stop hovering is a
  // pane that flickers.
  const shown = preview ?? current;

  async function select(avatar: AvatarOption) {
    setBusy(true);
    try {
      await api.post('/avatars/select', { avatarId: avatar.id });
      setPage((data) => (data ? { ...data, selectedId: avatar.id, hasLegacyAvatar: false } : data));
      push('success', `You are now ${avatar.displayName}.`);
      onSelected?.(avatar);
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save that avatar.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleFavorite(avatar: AvatarOption) {
    const next = !avatar.favorite;
    // Optimistic, because a star is a preference and the failure case is that
    // it flips back. Blocking the UI on a round trip for this would be worse
    // than being briefly wrong.
    setRows((list) =>
      list.map((row) => (row.id === avatar.id ? { ...row, favorite: next } : row)),
    );
    setPreview((row) => (row && row.id === avatar.id ? { ...row, favorite: next } : row));
    try {
      if (next) await api.post(`/avatars/${avatar.id}/favorite`, {});
      else await api.del(`/avatars/${avatar.id}/favorite`);
    } catch {
      setRows((list) =>
        list.map((row) => (row.id === avatar.id ? { ...row, favorite: !next } : row)),
      );
    }
  }

  if (error && rows.length === 0) {
    return <ErrorState message={error} onRetry={() => setOffset(0)} />;
  }

  return (
    <div className="apick">
      {/* ---- The preview. Sticky, because every tile below changes it. ---- */}
      <div className="apick__preview">
        <div className="pxframe apick__stage">
          <div className="tiny mono muted">TRAINER AVATAR</div>

          <div className="apick__stage-art">
            {shown ? (
              <img
                className="pixel apick__stage-img"
                src={shown.assetUrl}
                alt={shown.displayName}
                width={160}
                height={160}
                draggable={false}
              />
            ) : (
              <span className="apick__stage-none" aria-hidden>
                ◓
              </span>
            )}
          </div>

          {shown ? (
            <div className="stack-sm apick__stage-meta">
              <strong>{shown.displayName}</strong>
              <div className="row row--wrap apick__facts">
                <span className="chip chip--tiny">Gen {shown.generation}</span>
                <span className="chip chip--tiny">{shown.trainerClass}</span>
                {shown.presentation !== 'NEUTRAL' && (
                  <span className="chip chip--tiny">
                    {shown.presentation === 'MALE' ? 'Male' : 'Female'}
                  </span>
                )}
              </div>

              {/* Attribution, shown where the choice is made. Every sprite
                  shipped today is an official game rip with no individual
                  artist, so this normally reads as the source line — but the
                  artist branch is the one that matters and it is not optional. */}
              <div className="tiny muted apick__credit">
                {shown.artistCredit ? (
                  <>Sprite by {shown.artistCredit}</>
                ) : (
                  <>Source: Pokémon Showdown trainer sprites</>
                )}
                {' · '}
                <Link to="/credits">Credits</Link>
              </div>

              {shown.id === selectedId ? (
                <Button variant="ghost" disabled>
                  Currently worn
                </Button>
              ) : shown.unlocked ? (
                <Button variant="gold" onClick={() => void select(shown)} loading={busy}>
                  Select Avatar
                </Button>
              ) : (
                <div className="tiny muted">{shown.unlockNote ?? 'Not unlocked yet.'}</div>
              )}
            </div>
          ) : (
            <p className="tiny muted apick__stage-meta">
              Pick a trainer below to see them here.
            </p>
          )}
        </div>

        {page?.hasLegacyAvatar && (
          // Said once, and only to the people it is about. A player who chose a
          // Pokémon avatar years ago should not have to wonder why the new
          // system looks like it did nothing.
          <p className="tiny muted apick__legacy">
            Your profile still shows the Pokémon you picked as an avatar. Choosing a trainer
            here replaces it — nothing has been changed for you.
          </p>
        )}
      </div>

      {/* ---- The controls and the grid. ---- */}
      <div className="apick__browse stack">
        <div className="row row--wrap apick__filters">
          {CATEGORY_FILTERS.map((entry, index) => (
            <button
              key={entry.label}
              type="button"
              className={`chip ${filter === index ? 'chip--on' : ''}`}
              onClick={() => setFilter(index)}
            >
              {entry.label}
            </button>
          ))}
          <button
            type="button"
            className={`chip ${favoritesOnly ? 'chip--on' : ''}`}
            onClick={() => setFavoritesOnly((on) => !on)}
            aria-pressed={favoritesOnly}
          >
            ★ Favourites
          </button>
        </div>

        <div className="row row--wrap apick__search">
          <input
            className="input apick__searchbox"
            type="search"
            value={search}
            placeholder="Search trainers and classes…"
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search trainer avatars"
          />
          <select
            className="input apick__select"
            value={trainerClass}
            onChange={(event) => setTrainerClass(event.target.value)}
            aria-label="Trainer class"
          >
            <option value="">Every class</option>
            {(page?.classes ?? []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="input apick__select"
            value={presentation}
            onChange={(event) => setPresentation(event.target.value)}
            aria-label="Presentation"
          >
            {PRESENTATIONS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>

        {loading && rows.length === 0 ? (
          <LoadingGrid count={12} height={96} />
        ) : rows.length === 0 ? (
          <p className="tiny muted">
            {favoritesOnly
              ? 'No favourites yet. Star a trainer to keep them here.'
              : 'No trainers match that.'}
          </p>
        ) : (
          <>
            <div className="apick__grid">
              {rows.map((avatar) => (
                <AvatarTile
                  key={avatar.id}
                  avatar={avatar}
                  worn={avatar.id === selectedId}
                  focused={preview?.id === avatar.id}
                  onPreview={() => setPreview(avatar)}
                  onFavorite={() => void toggleFavorite(avatar)}
                />
              ))}
            </div>

            <div className="row row--between apick__more">
              <span className="tiny muted">
                Showing {rows.length} of {page?.total ?? rows.length}
              </span>
              {page && rows.length < page.total && (
                <Button
                  variant="ghost"
                  loading={loading}
                  onClick={() => setOffset(rows.length)}
                >
                  Show more
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One trainer in the grid.
 *
 * Clicking previews rather than selecting. That is the brief's "show a larger
 * preview before saving", and it also removes the one genuinely bad outcome of
 * a dense sprite grid on a phone: a mis-tap silently changing your identity.
 * Selecting is a separate button in the preview, at a size a thumb can hit.
 *
 * The star is a button inside a button, which is why it stops propagation —
 * favouriting must not also change what the preview is showing.
 */
function AvatarTile({
  avatar,
  worn,
  focused,
  onPreview,
  onFavorite,
}: {
  avatar: AvatarOption;
  worn: boolean;
  focused: boolean;
  onPreview: () => void;
  onFavorite: () => void;
}) {
  return (
    <div
      className={[
        'atile',
        worn ? 'atile--worn' : '',
        focused ? 'atile--focused' : '',
        avatar.unlocked ? '' : 'atile--locked',
      ].join(' ')}
    >
      <button
        type="button"
        className="atile__btn"
        onClick={onPreview}
        title={avatar.unlocked ? avatar.displayName : (avatar.unlockNote ?? 'Locked')}
        aria-pressed={worn}
      >
        <span className="atile__art">
          <img
            className="pixel atile__img"
            src={avatar.assetUrl}
            alt=""
            // The source's own size. Drawn 1:1 so nothing is ever resampled.
            width={80}
            height={80}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        </span>
        <span className="atile__name tiny">{avatar.displayName}</span>
        {worn && <span className="atile__worn tiny mono">WORN</span>}
        {!avatar.unlocked && (
          <span className="atile__lock" aria-hidden>
            🔒
          </span>
        )}
      </button>

      <button
        type="button"
        className={`atile__fav ${avatar.favorite ? 'atile__fav--on' : ''}`}
        onClick={(event) => {
          event.stopPropagation();
          onFavorite();
        }}
        aria-label={avatar.favorite ? `Unfavourite ${avatar.displayName}` : `Favourite ${avatar.displayName}`}
        aria-pressed={avatar.favorite}
      >
        ★
      </button>
    </div>
  );
}
